/**
 * Ingestion layer — turns whatever the backend puts in `summary` into
 * `{ tree, prose }` without assuming a schema.
 *
 * Real-world shapes this must survive:
 *  - a plain object (future backends may return JSON directly)
 *  - a JSON string
 *  - a Python dict repr string: {'key': 'value', 'n': None, 'ok': True}
 *  - prose followed by an embedded JSON block (current founder summaries)
 *  - plain prose / markdown
 */

export type TIngestResult = {
  /** Structured payload when one could be recovered. */
  tree: Record<string, unknown> | null;
  /** Free text that accompanied (or constituted) the payload. */
  prose: string | null;
};

/**
 * Convert a Python dict/list repr into JSON with a character-level scanner.
 * A regex swap breaks on apostrophes inside strings; this walks the string
 * tracking quote state instead.
 */
export function pythonReprToJson(raw: string): string | null {
  const src = raw.trim();
  if (!src.startsWith("{") && !src.startsWith("[")) return null;

  let out = "";
  let i = 0;
  let inString: '"' | "'" | null = null;

  while (i < src.length) {
    const ch = src[i];

    if (inString) {
      if (ch === "\\") {
        const next = src[i + 1] ?? "";
        // \' inside a single-quoted Python string is a literal apostrophe
        if (inString === "'" && next === "'") {
          out += "'";
        } else {
          out += ch + next;
        }
        i += 2;
        continue;
      }
      if (ch === inString) {
        out += '"';
        inString = null;
        i += 1;
        continue;
      }
      // A double quote inside a single-quoted Python string must be escaped in JSON
      if (inString === "'" && ch === '"') {
        out += '\\"';
        i += 1;
        continue;
      }
      // Normalize raw newlines that would be invalid inside JSON strings
      if (ch === "\n") {
        out += "\\n";
        i += 1;
        continue;
      }
      out += ch;
      i += 1;
      continue;
    }

    if (ch === "'" || ch === '"') {
      inString = ch;
      out += '"';
      i += 1;
      continue;
    }

    // Bare word constants outside strings
    if (src.startsWith("None", i)) {
      out += "null";
      i += 4;
      continue;
    }
    if (src.startsWith("True", i)) {
      out += "true";
      i += 4;
      continue;
    }
    if (src.startsWith("False", i)) {
      out += "false";
      i += 5;
      continue;
    }

    out += ch;
    i += 1;
  }

  return out;
}

function tryJsonParse(candidate: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(candidate) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // caller falls through to the next strategy
  }
  return null;
}

function tryParseObjectString(candidate: string): Record<string, unknown> | null {
  const direct = tryJsonParse(candidate);
  if (direct) return direct;

  // JSON with invalid \' escapes leaked from the AI
  const deEscaped = tryJsonParse(candidate.replace(/\\'/g, "’"));
  if (deEscaped) return deEscaped;

  const asJson = pythonReprToJson(candidate);
  if (asJson) {
    const parsed = tryJsonParse(asJson);
    if (parsed) return parsed;
  }
  return null;
}

/**
 * Find the largest balanced `{...}` block in mixed prose and try to parse it.
 * Returns the parsed object plus the surrounding prose with the block removed.
 */
function extractEmbeddedObject(raw: string): { tree: Record<string, unknown>; prose: string } | null {
  for (let start = raw.indexOf("{"); start !== -1; start = raw.indexOf("{", start + 1)) {
    let depth = 0;
    let inString = false;
    for (let i = start; i < raw.length; i++) {
      const ch = raw[i];
      if (inString) {
        if (ch === "\\") i += 1;
        else if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') inString = true;
      else if (ch === "{") depth += 1;
      else if (ch === "}") {
        depth -= 1;
        if (depth === 0) {
          const candidate = raw.slice(start, i + 1);
          const tree = tryParseObjectString(candidate);
          if (tree) {
            const prose = (raw.slice(0, start) + raw.slice(i + 1)).trim();
            return { tree, prose };
          }
          break; // balanced but unparseable — try the next `{`
        }
      }
    }
  }
  return null;
}

/** Parse an arbitrary summary payload into a structured tree and/or prose. */
export function ingestSummaryPayload(raw: unknown): TIngestResult {
  if (raw == null) return { tree: null, prose: null };

  if (typeof raw === "object" && !Array.isArray(raw)) {
    return { tree: raw as Record<string, unknown>, prose: null };
  }

  if (typeof raw !== "string") {
    const prose = typeof raw === "number" || typeof raw === "boolean" || typeof raw === "bigint" ? String(raw) : null;
    return { tree: null, prose };
  }

  const trimmed = raw.trim();
  if (!trimmed) return { tree: null, prose: null };

  // Whole string is an object (JSON or Python repr)
  if (trimmed.startsWith("{")) {
    const tree = tryParseObjectString(trimmed);
    if (tree) return { tree, prose: null };
  }

  // Prose with an embedded object block
  const embedded = extractEmbeddedObject(trimmed);
  if (embedded) {
    return { tree: embedded.tree, prose: embedded.prose || null };
  }

  return { tree: null, prose: trimmed };
}
